import { useMemo } from 'react'
import { useFloorPlanStore } from '@/features/floor-plan'
import { useTableCardsQuery } from './useTableCardsQuery'
import { TableModel } from '../api/TableCardsManagementAPI.model'
// QR images are no longer pulled from the backend, the frontend generates the
// code from `tableId` (see buildTableQrValue / TableCardQrCode).

export interface PlannerTableRow {
  /** Planner table id, the stable selection key in the QR grid. */
  id: string
  /** Planner table label parsed as a number (its "table number"). */
  number: number
  /** Name of the room (sala) the table belongs to. */
  room: string
  /** Backend table id of the matching table, the QR encodes this. */
  tableId?: string
  /** Card linked on the matching backend table, if any. */
  qrCodeCardId?: string
  /** True when a backend table with the same number exists (so it can print). */
  hasQr: boolean
}

/**
 * Bridges the floor-plan planner and the table-cards (QR) feature: the table
 * list shown for QR printing comes from the planner, while the printable QR
 * image is pulled from the backend table whose `number` matches the planner
 * table's label. Planner tables without a matching backend number have no QR.
 */
export function usePlannerTables(): PlannerTableRow[] {
  const plannerTables = useFloorPlanStore(s => s.tables)
  const rooms = useFloorPlanStore(s => s.rooms)
  const { tablesList } = useTableCardsQuery()

  return useMemo(() => {
    const backendByNumber = new Map<number, TableModel>()
    tablesList?.forEach(table => backendByNumber.set(table.number, table))

    const roomNameById = new Map(rooms.map(room => [room.id, room.name]))

    return plannerTables.map(table => {
      const number = Number(table.label)
      const backend = Number.isFinite(number)
        ? backendByNumber.get(number)
        : undefined

      return {
        id: table.id,
        number,
        room: roomNameById.get(table.roomId) ?? '',
        tableId: backend?.id,
        qrCodeCardId: backend?.qrCodeCardId,
        hasQr: Boolean(backend)
      }
    })
  }, [plannerTables, rooms, tablesList])
}
